/**
 * The shared footer of the three preview cards: "x von y" plus "Mehr laden".
 *
 * One component because the three cards must behave identically — the user reads
 * them as one row, and three hand-rolled footers is how they drift apart.
 *
 * "Mehr laden" refetches this card's section with a larger limit rather than
 * appending a second page. That keeps the server's ordering authoritative (an
 * appended page can duplicate or skip rows when the underlying set changed
 * between calls) at the cost of re-reading a handful of rows — a trade worth
 * making at these sizes, and one that leaves the request bounded either way.
 *
 * `linkTo` is OPTIONAL and stays absent for the cards whose "show all" target
 * from the design reference is still a page that does not exist. A card passes it
 * once its destination is routed — the sync card did when `/admin/bexio/sync-runs`
 * shipped. Cards that pass nothing render exactly as before.
 */
import React from "react";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function BexioPreviewFooter({
  shown,
  total,
  noun,
  nounPlural,
  onLoadMore,
  loadingMore = false,
  linkTo,
  linkLabel,
}) {
  const hasTotal = typeof total === "number";
  // Without a total we cannot say whether anything is left, so we do not offer
  // to load more — a button that might do nothing is worse than no button.
  const canLoadMore = Boolean(onLoadMore) && hasTotal && shown < total;

  if (!hasTotal && !canLoadMore && !linkTo) return null;

  return (
    <div className="mt-auto flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-xs text-muted-foreground">
      <span>
        {hasTotal ? (
          <>
            {shown} von {total} {total === 1 ? noun : nounPlural}
          </>
        ) : (
          <>
            {shown} {shown === 1 ? noun : nounPlural}
          </>
        )}
      </span>

      <span className="flex shrink-0 items-center gap-3">
        {linkTo ? (
          <Link
            to={linkTo}
            className="whitespace-nowrap rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {linkLabel}
          </Link>
        ) : null}
      {canLoadMore ? (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loadingMore}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loadingMore ? (
            <Loader2 aria-hidden="true" className="h-3 w-3 animate-spin" />
          ) : null}
          {loadingMore ? "Lädt …" : "Mehr laden"}
        </button>
      ) : null}
      </span>
    </div>
  );
}
